import type {PayloadAction} from "@reduxjs/toolkit";
import {createSlice} from "@reduxjs/toolkit";

import type {UserProfile} from "../api/payload-types/user-profile";

interface AccountManagementState {
    /** Last GET/PUT result; null while unknown (not loaded yet, or the last attempt failed) — treated as not editable. */
    profile: UserProfile | null;
    profileDialogOpen: boolean;
}

const initialState: AccountManagementState = {
    profile: null,
    profileDialogOpen: false,
};

/**
 * #19351: UI/data state for the in-REVA account-management profile dialog, which replaces the header
 * user menu's link to accounts.immobrain.net. Shared by {@link HeaderUserMenu} (decides whether the menu
 * entry is usable), {@link ProfileDialog} itself, and {@link EmailVerificationNotice} (opens the profile
 * dialog instead of linking out).
 */
export const accountManagementSlice = createSlice({
    name: "accountManagement",
    initialState,
    reducers: {
        setUserProfile: (state, action: PayloadAction<UserProfile | null>) => {
            state.profile = action.payload;
        },
        openProfileDialog: (state) => {
            state.profileDialogOpen = true;
        },
        closeProfileDialog: (state) => {
            state.profileDialogOpen = false;
        },
    },
    selectors: {
        selectUserProfile: (state) => state.profile,
        selectUserProfileEditable: (state) => state.profile?.editable ?? false,
        selectProfileDialogOpen: (state) => state.profileDialogOpen,
    },
});

export const {setUserProfile, openProfileDialog, closeProfileDialog} = accountManagementSlice.actions;

export const {selectUserProfile, selectUserProfileEditable, selectProfileDialogOpen} = accountManagementSlice.selectors;

export default accountManagementSlice.reducer;
